import { Box } from '@material-ui/core';
import { PaymentMethodType } from '@nemuru-eng/js-api-client';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { useCheckoutSessionContext } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/_shared/contexts/CheckoutSessionContext';
import { NextActions } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/_shared/components/NextActions/NextActions';
import { CardOnboardingPaymentMethodDetailEntity } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/Loan/CardOnboarding/_shared/entities/PaymentMethodDetail/CardOnboardingPaymentMethodDetailEntity';
import { ConditionsAndProgress } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/Loan/CardOnboarding/ConditionsAndProgress/ConditionsAndProgress';
import {
  NextActionFactory,
  NextActionFactoryProps,
} from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/Loan/CardOnboarding/NextActions/components/NextActionFactory';
import { useLoanPaymentIntent } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/Loan/Loan/_shared/hooks/useLoanPaymentIntent';
import { MerchantPosDialog } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/Loan/Loan/PaymentIntent/Multilender/MerchantPos/MerchantPosDialog';
import { CustomSkeleton } from '@/components/shared/components/CustomSkeleton';

interface CardOnboardingPaymentMethodProps {
  paymentMethodType: PaymentMethodType;
}

export const CardOnboardingPaymentMethod = ({ paymentMethodType }: CardOnboardingPaymentMethodProps) => {
  const { paymentMethodDetailEntities } = useCheckoutSessionContext();

  const { refreshPaymentMethodEntity } = useLoanPaymentIntent();

  const [loading, setLoading] = useState(true);
  const [merchantPosOpened, setMerchantPosOpened] = useState(false);

  const openMerchantPos = useCallback(() => setMerchantPosOpened(true), []);

  const CardOnboardingNextActionFactory = useMemo(
    () => (props: Omit<NextActionFactoryProps, 'onOpenMerchantPos'>) =>
      <NextActionFactory {...props} onOpenMerchantPos={openMerchantPos} />,
    [openMerchantPos],
  );

  const paymentMethodDetailEntity = useMemo(
    () =>
      paymentMethodDetailEntities.find((pme) => pme.type === paymentMethodType) as
        | CardOnboardingPaymentMethodDetailEntity
        | undefined,
    [paymentMethodDetailEntities, paymentMethodType],
  );

  useEffect(() => {
    if (!paymentMethodDetailEntity) return;
    setLoading(false);
  }, [paymentMethodDetailEntity]);

  useEffect(() => {
    const refresh = async () => {
      if (
        paymentMethodDetailEntity &&
        paymentMethodDetailEntity.isSelected === false &&
        paymentMethodDetailEntity.isRefreshed === false &&
        paymentMethodDetailEntities.length > 0
      ) {
        await refreshPaymentMethodEntity(paymentMethodDetailEntity);
      }
    };

    refresh();
  }, [paymentMethodDetailEntity]);

  const cardOnboardingPaymentMethodDetailEntity =
    paymentMethodDetailEntity instanceof CardOnboardingPaymentMethodDetailEntity
      ? paymentMethodDetailEntity
      : undefined;

  const showSkeletons =
    !cardOnboardingPaymentMethodDetailEntity || !cardOnboardingPaymentMethodDetailEntity.isRefreshed || loading;

  return (
    <Box display="flex" flexDirection="column" gridGap={24}>
      {showSkeletons ? (
        <>
          <CustomSkeleton variant="rect" width="100%" height="268px" />
          <CustomSkeleton variant="rect" width="100%" height="295px" />
        </>
      ) : (
        <>
          <ConditionsAndProgress
            paymentMethodDetailEntity={cardOnboardingPaymentMethodDetailEntity}
            onOpenMerchantPos={openMerchantPos}
          />
          <NextActions
            paymentMethodDetailEntity={cardOnboardingPaymentMethodDetailEntity}
            NextActionFactory={CardOnboardingNextActionFactory}
          />
        </>
      )}
      {cardOnboardingPaymentMethodDetailEntity ? (
        <MerchantPosDialog
          opened={merchantPosOpened}
          setOpened={setMerchantPosOpened}
          paymentMethodDetailEntity={cardOnboardingPaymentMethodDetailEntity}
        />
      ) : null}
    </Box>
  );
};
